import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { IMAGES, DRINKS } from "@/lib/site";
import { Reveal } from "@/components/site/Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Products() {
  const [active, setActive] = useState(null);
  const openDrink = active ? DRINKS.find((d) => d.name === active) : null;

  return (
    <section id="menu" data-testid="menu" className="relative bg-[#C2E9FF]/40 text-[#17320B] py-24 md:py-40 border-t border-[#17320B]/10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <Reveal>
            <span className="font-mono-eb text-xs uppercase tracking-[0.3em] text-[#7B9C62]">/ The Menu</span>
            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9] mt-5">
              Pick Your <span className="text-[#7B9C62]">Fuel</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-[#17320B]/70 text-base md:text-lg">
              Six clean blends {"\u2014"} protein shakes, ceremonial matcha and cold-brew iced tea.
              Tap any drink for full macros and ingredients.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6">
          {DRINKS.map((d, i) => (
            <Reveal key={d.name} delay={i * 0.05} className={d.span}>
              <motion.button
                type="button"
                onClick={() => setActive(d.name)}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                data-testid={`drink-${d.name.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative h-full w-full min-h-[360px] overflow-hidden rounded-sm border border-[#17320B]/10 bg-white flex text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#17320B]"
              >
                <div className="relative w-2/5 shrink-0 overflow-hidden bg-[#D9F4CD]">
                  <img
                    src={d.img}
                    alt={`${d.name} ${d.line}`}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="relative flex-1 p-7 md:p-8 flex flex-col justify-between">
                  <div
                    className="absolute -right-14 -top-14 h-44 w-44 rounded-full blur-2xl opacity-15 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ background: d.color }}
                  />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-mono-eb text-[10px] uppercase tracking-[0.2em] text-[#7B9C62] mb-2">{d.line}</div>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#17320B]/15 text-[#17320B] group-hover:bg-[#17320B] group-hover:border-[#17320B] group-hover:text-[#D9F4CD] transition-colors duration-300">
                        <Plus size={16} />
                      </span>
                    </div>
                    <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-none mb-3 text-[#17320B]">{d.name}</h3>
                    <p className="text-[#17320B]/60 text-sm leading-relaxed max-w-xs">{d.desc}</p>
                  </div>
                  <div className="relative mt-6 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="font-mono-eb text-[10px] uppercase tracking-[0.12em] text-[#17320B]/60">{d.macro}</span>
                      <span className="font-mono-eb text-[10px] uppercase tracking-[0.12em] text-[#17320B]/60">{d.kcal}</span>
                    </div>
                  </div>
                </div>
              </motion.button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-6 relative overflow-hidden rounded-sm">
            <img src={IMAGES.gym} alt="MacroBar shake in gym" className="h-[280px] md:h-[420px] w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(23,50,11,0.78), transparent 60%)" }} />
            <div className="absolute inset-0 flex items-center p-8 md:p-16">
              <div className="max-w-lg text-white">
                <div className="font-mono-eb text-[10px] uppercase tracking-[0.25em] text-[#C2E9FF] mb-3">Instant {"\u00b7"} Healthy {"\u00b7"} Tasty</div>
                <div className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">
                  Blended to order, never sitting on a shelf.
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Nutrition dialog */}
      <Dialog open={!!openDrink} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="nutrition-dialog"
          className="max-w-2xl p-0 overflow-hidden border-[#17320B]/10 bg-white text-[#17320B] gap-0"
        >
          {openDrink && (
            <div className="grid sm:grid-cols-[0.85fr_1.15fr]">
              <div className="relative hidden sm:block bg-[#D9F4CD]">
                <img src={openDrink.img} alt={openDrink.name} className="absolute inset-0 h-full w-full object-cover" />
              </div>

              <div className="p-7 md:p-9">
                <DialogHeader className="space-y-0 text-left">
                  <div className="font-mono-eb text-[10px] uppercase tracking-[0.2em] text-[#7B9C62]">{openDrink.line}</div>
                  <DialogTitle className="font-display uppercase text-4xl tracking-tight leading-none mt-2 text-[#17320B]">
                    {openDrink.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-3 flex items-center gap-4">
                  <span className="font-mono-eb text-[10px] uppercase tracking-[0.15em] text-[#17320B]/50">
                    Serving {openDrink.serving}
                  </span>
                </div>

                <p className="mt-4 text-[#17320B]/60 text-sm leading-relaxed">{openDrink.desc}</p>

                <div className="mt-6">
                  <div className="font-mono-eb text-[10px] uppercase tracking-[0.2em] text-[#7B9C62] mb-3">Nutrition</div>
                  <div className="divide-y divide-[#17320B]/10 border-y border-[#17320B]/10">
                    {openDrink.nutrition.map((n) => (
                      <div key={n.label} data-testid={`nutrition-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center justify-between py-2.5">
                        <span className={`text-sm ${n.label.startsWith("of which") ? "text-[#17320B]/40 pl-3" : "text-[#17320B]/70"}`}>{n.label}</span>
                        <span className="font-display text-lg tracking-tight text-[#17320B]">{n.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="font-mono-eb text-[10px] uppercase tracking-[0.2em] text-[#7B9C62] mb-3">Ingredients</div>
                  <div className="flex flex-wrap gap-2">
                    {openDrink.ingredients.map((ing) => (
                      <span key={ing} className="rounded-full border border-[#17320B]/15 bg-[#D9F4CD]/60 px-3 py-1 text-xs text-[#17320B]/80">
                        {ing}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-[#17320B]/40 leading-relaxed">{openDrink.allergens}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
